import { useState, useEffect, useCallback } from 'react';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';
import ParcelRatesTab from './ParcelRatesTab';
import { periodGrid } from '../utils/periods';

// ── Period generation (28-day periods, same anchor as other pages) ─────────────
// Walks out from TODAY's period (periodGrid), not from the anchor: a fixed
// -24…+6 window around 16 May 2026 ran out on 31 Oct 2026, hiding the current period.
function generatePeriods() {
  return periodGrid({ back: 24, forward: 6 });
}
const PERIODS = generatePeriods();
function findCurrentPeriod() {
  return PERIODS.find(p => p.current) || PERIODS[0];
}

function formatPeriod(p) {
  if (!p) return p;
  if (String(p).includes('T')) {
    const d = new Date(p);
    const end = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const start = new Date(d);
    start.setDate(d.getDate() - 27);
    const startFmt = start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startFmt} to ${end}`;
  }
  return p;
}

// "22 Mar 2026 to 18 Apr 2026" → "2026-04-18" (period END as ISO). Handles
// abbreviated/full month names. GCCD history records store this label, so we match
// on the end date — the old exact-string compare against "D-Mon-YYYY" never matched.
function labelToEndISO(label) {
  const part = (label || '').toString().split(' to ')[1] || '';
  const m = part.trim().match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (!m) return '';
  const mon = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' }[m[2].slice(0, 3).toLowerCase()];
  return mon ? `${m[3]}-${mon}-${m[1].padStart(2, '0')}` : '';
}

function formatMoney(n) {
  if (!n && n !== 0) return '$0.00';
  const num = parseFloat(n);
  const abs = Math.abs(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return num < 0 ? '-$' + abs : '$' + abs;
}

function ceilTo(value, precision) {
  const factor = Math.pow(10, precision);
  // Strip IEEE-754 noise (e.g. 0.07*100 = 7.000000000000001) before ceiling so a
  // clean 2-dp rate isn't bumped up a whole cent — that over-charges per unit.
  return Math.ceil(Number((value * factor).toFixed(6))) / factor;
}

function StatusBadge({ status }) {
  const cfg = {
    OVER:    { bg: '#d1fae5', color: '#065f46', label: 'At / Over Min' },
    AT_MIN:  { bg: '#d1fae5', color: '#065f46', label: 'At Min' },
    UNDER:   { bg: '#fee2e2', color: '#991b1b', label: 'Under Min' },
    NO_RATE: { bg: '#fef3c7', color: '#92400e', label: 'No CTTCD Rate' },
  }[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: cfg.bg, color: cfg.color, letterSpacing: '0.03em' }}>
      {cfg.label}
    </span>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', borderTop: `4px solid ${accent}` }}>
      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: '800', color: accent, margin: '8px 0 4px', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

function GCCDHistoryTab({ records, loading, error, onRefresh, selectedPeriodISO }) {
  const periods = [...new Set(records.map(r => r.period))].sort();

  // Convert the selected period ISO end date to the "DD-Mon-YYYY" format the sheet uses
  const selectedSheetPeriod = (() => {
    if (!selectedPeriodISO) return '';
    const [y, m, d] = selectedPeriodISO.split('-').map(Number);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d}-${months[m-1]}-${y}`;
  })();

  // Match stored records to the selected period by their END date — records store a
  // "Start to End" label, so the old exact-string compare never matched (also accept
  // the legacy DD-Mon / ISO formats just in case).
  const filtered = records.filter(r =>
    labelToEndISO(r.period) === selectedPeriodISO ||
    r.period === selectedSheetPeriod ||
    r.period === selectedPeriodISO
  );
  const periodOnRecord = filtered.length > 0;

  // Live fallback: when nothing is recorded yet for the period (RCTIs not built),
  // show the same live assessment the Uplift Workbench computes, so it's never blank.
  const [livePreview, setLivePreview] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (loading || error || !selectedPeriodISO || filtered.length > 0) { setLivePreview([]); return; }
    setLiveLoading(true);
    fetch('/.netlify/functions/gccd-uplift-sb', { method: 'POST', body: JSON.stringify({ action: 'getUpliftWorkbenchData', period: selectedPeriodISO }) })
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        setLivePreview((j && j.success ? j.drivers || [] : []).map(d => ({
          period: (j && j.periodLabel) || selectedPeriodISO, driver: d.driver, company: d.driverName || '',
          grossIncome: d.actualPayable, leaseFee: 0, netPayable: d.actualPayable, minPayable: d.minPayable,
          variance: d.variance, topUp: Math.max(0, -(d.variance || 0)),
          status: (d.variance || 0) >= 0 ? 'Meets Minimum' : 'Top-Up', _live: true,
        })));
      })
      .catch(() => { if (!cancelled) setLivePreview([]); })
      .finally(() => { if (!cancelled) setLiveLoading(false); });
    return () => { cancelled = true; };
  }, [selectedPeriodISO, filtered.length, loading, error]);

  const isLive = filtered.length === 0 && livePreview.length > 0;
  const displayRecords = filtered.length ? filtered : livePreview;

  // Read-only driver profile photos for the CTTCD table. displayRecords is a fresh
  // array each render, so key the effect on a stable joined-logins string.
  const [photos, setPhotos] = useState({});
  const photoLogins = [...new Set((displayRecords || []).map(x => (x.driver || x.login || x.loginName || x.driverLogin || '').toUpperCase()).filter(Boolean))].join(',');
  useEffect(() => {
    const logins = photoLogins ? photoLogins.split(',') : [];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [photoLogins]);

  const totalTopUp = displayRecords.reduce((sum, r) => sum + (r.topUp > 0 ? r.topUp : 0), 0);
  const overpayingPool = displayRecords.reduce((sum, r) => sum + (r.variance > 0 ? r.variance : 0), 0);
  const meetsMin = displayRecords.filter(r => r.status === 'Meets Minimum' || r.variance >= 0).length;
  const needsTopUp = displayRecords.filter(r => r.topUp > 0).length;
  const totalDrivers = displayRecords.length;

  return (
    <div>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
          {formatPeriod(selectedSheetPeriod || selectedPeriodISO)}
        </span>
        <button onClick={onRefresh} style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#1a3a6b', color: '#fff', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>↻ Refresh</button>
      </div>
      {loading && <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading CTTCD history...</div>}
      {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '16px', color: '#dc2626', marginBottom: '24px' }}>{error}</div>}
      {!loading && !error && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px', marginBottom: '32px' }}>
            <SummaryCard label="Total Top-Up Cost" value={formatMoney(totalTopUp)} sub={`${needsTopUp} driver${needsTopUp !== 1 ? 's' : ''} require top-up`} accent="#1a3a6b" />
            <SummaryCard label="Overpaying Pool" value={formatMoney(overpayingPool)} sub="Excess above minimum" accent="#059669" />
            <SummaryCard label="Meets Minimum" value={`${meetsMin} / ${totalDrivers}`} sub={`${totalDrivers > 0 ? Math.round((meetsMin / totalDrivers) * 100) : 0}% compliance rate`} accent="#7c3aed" />
            <SummaryCard label="Periods on Record" value={periods.length} sub="Billing periods captured" accent="#f59e0b" />
          </div>
          {displayRecords.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280', background: '#f9fafb', borderRadius: '10px' }}>
              {liveLoading ? 'Loading period data…' : 'No CTTCD data for this period yet. Build RCTIs (or import this period’s activity) to populate.'}
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              {isLive && (
                <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde047', padding: '10px 20px', color: '#92400e', fontSize: '13px', fontWeight: 600 }}>
                  ⚡ Live preview — this period isn’t recorded to CTTCD history yet. Build RCTIs to lock these figures in.
                </div>
              )}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>Driver Records — {formatPeriod(selectedSheetPeriod || selectedPeriodISO)}{isLive ? ' (live)' : ''}</h2>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{displayRecords.length} records</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Period','Driver','Company','Gross Income','Lease Fee','Net Payable','Min Payable','Variance','Top Up','Status'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: ['Period','Driver','Company','Status'].includes(h) ? 'left' : 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRecords.map((r, i) => {
                      const isTopUp = r.topUp > 0;
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{formatPeriod(r.period)}</td>
                          <td style={{ padding: '10px 14px', color: '#374151', fontWeight: '500' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <DriverAvatar login={r.driver} photo={photos[(r.driver || '').toUpperCase()]} size={24} />
                              <span>{r.driver}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 14px', color: '#6b7280' }}>{r.company || '—'}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#374151' }}>{formatMoney(r.grossIncome)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#dc2626' }}>{r.leaseFee > 0 ? formatMoney(r.leaseFee) : '—'}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#374151' }}>{formatMoney(r.netPayable)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#374151' }}>{formatMoney(r.minPayable)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: r.variance >= 0 ? '#059669' : '#dc2626', fontWeight: '600' }}>{formatMoney(r.variance)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: isTopUp ? '#d97706' : '#6b7280', fontWeight: isTopUp ? '600' : '400' }}>{isTopUp ? formatMoney(r.topUp) : '—'}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: isTopUp ? '#fef3c7' : '#d1fae5', color: isTopUp ? '#92400e' : '#065f46' }}>
                              {isTopUp ? 'Top-Up' : 'Meets Min'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={{ marginTop: '24px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '16px 20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '20px' }}>📋</span>
            <div>
              <div style={{ fontWeight: '700', color: '#1e40af', fontSize: '14px', marginBottom: '4px' }}>Australia Post Tender — June 2027</div>
              <div style={{ color: '#3b82f6', fontSize: '13px' }}>This report captures 18 months of CTTCD compliance data to support the tender negotiation. Every RCTI build automatically records to this history.</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Where a driver's hours came from. Portal shift times are what we pay on; 'mixed' means the
// scanner filled days the portal never captured; 'agreed' is a manual Hourly Drivers override.
const HOURS_BASIS_LABEL = {
  reported: 'agreed',
  portal: 'portal',
  mixed: 'portal + scanner fill',
  scanner: 'scanner',
  decided: 'reviewed',
};

const UNIT_TYPES = ['Parcels', 'Express', 'Depot Bags', 'DoE'];
const UNIT_TYPE_KEY = { Parcels: 'parcels', Express: 'express', 'Depot Bags': 'depotBags', DoE: 'doe' };

// ── Period-end hours review ───────────────────────────────────────────────────
// Portal and scanner disagree about a day and either can be wrong: a driver who forgets to
// clock on makes the portal too late, and the scanner's start is only the "usual start" rule.
// The adjudicator is the driver's FIRST DEVICE SCAN — they can't scan a parcel before they
// started. Paul decides the flagged days here at cut-off; the call drives the minimum-pay
// maths (and so the uplift and the RCTI), and leaves both original records untouched.
const FLAG_TEXT = {
  SCAN_BEFORE_CLOCK: { label: 'Scanned before clock-on', tone: '#b45309', why: 'The portal clock-on is later than the first parcel scan, so the driver was already working. Portal start looks too late.' },
  NO_PORTAL: { label: 'No portal shift', tone: '#b91c1c', why: 'The driver never clocked on, so this day rests entirely on the scanner\'s assumed start time.' },
  LONG_PRESORT: { label: 'Long gap to first scan', tone: '#6b7280', why: 'Clocked on well before the first scan. Usually a long indoor sort — but worth an eye.' },
};
const hhmmToMin = (v) => { const m = String(v || '').match(/^(\d{1,2}):(\d{2})$/); return m ? +m[1] * 60 + +m[2] : null; };
const hoursBetween = (start, finish) => {
  const a = hhmmToMin(start), b = hhmmToMin(finish);
  if (a == null || b == null) return null;
  return Math.round(((b - a) / 60 + (b < a ? 24 : 0)) * 100) / 100;
};
const dayText = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] + ' ' + d.getDate() + ' ' + ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
};

function HoursReviewDay({ period, driver, day, onSaved }) {
  const [manualStart, setManualStart] = useState(day.decision?.source === 'manual' ? day.decision.startTime : (day.scanner?.firstScan || ''));
  const [note, setNote] = useState(day.decision?.note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const finish = day.portal?.finish || day.scanner?.finish || '';
  const manualHours = hoursBetween(manualStart, finish);
  const dec = day.decision;
  const meta = FLAG_TEXT[day.flag] || null;

  const save = async (source, hours, startTime) => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', {
        method: 'POST',
        body: JSON.stringify({
          action: 'saveHourDecision', period, driver, day: day.date, source, hours,
          startTime: startTime || '', finishTime: finish, flag: day.flag, note,
          portalHours: day.portal?.hours ?? null, scannerHours: day.scanner?.hours ?? null,
          decidedBy: 'paul@hollypaul.com',
        }),
      });
      const json = await res.json();
      if (!json.success) { setErr(json.error || 'Save failed'); return; }
      await onSaved();
    } catch (e) { setErr('Save failed: ' + e.message); } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', {
        method: 'POST', body: JSON.stringify({ action: 'clearHourDecision', driver, day: day.date }),
      });
      const json = await res.json();
      if (!json.success) { setErr(json.error || 'Clear failed'); return; }
      await onSaved();
    } catch (e) { setErr('Clear failed: ' + e.message); } finally { setBusy(false); }
  };

  const choiceBtn = (active, disabled, onClick, title, lines) => (
    <button onClick={onClick} disabled={busy || disabled} title={title}
      style={{
        flex: '1 1 150px', textAlign: 'left', padding: '10px 12px', borderRadius: '8px', cursor: busy || disabled ? 'default' : 'pointer',
        border: active ? '2px solid #1a3a6b' : '1px solid #d1d5db', background: active ? '#eff6ff' : disabled ? '#f9fafb' : '#fff',
        opacity: disabled ? 0.5 : 1,
      }}>
      {lines}
    </button>
  );

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', marginBottom: '12px', background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px' }}>
        <strong style={{ fontSize: '14px' }}>{dayText(day.date)}</strong>
        {meta && <span style={{ fontSize: '12px', fontWeight: 700, color: meta.tone }}>⚑ {meta.label}</span>}
      </div>
      {meta && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{day.detail || meta.why}</div>}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
        {choiceBtn(dec?.source === 'portal' || (!dec && day.used === 'portal'), !day.portal,
          () => save('portal', day.portal.hours, day.portal.start), 'Pay on the portal shift the driver clocked',
          <>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em' }}>Portal</div>
            <div style={{ fontSize: '13px', fontWeight: 700 }}>{day.portal ? `${day.portal.start}–${day.portal.finish}` : 'No shift'}</div>
            <div style={{ fontSize: '12px', color: '#374151' }}>{day.portal ? day.portal.hours.toFixed(2) + ' hrs' : '—'}</div>
          </>)}
        {choiceBtn(dec?.source === 'scanner' || (!dec && day.used === 'scanner'), !day.scanner,
          () => save('scanner', day.scanner.hours, day.scanner.start), 'Pay on the scanner day (start is the usual-start rule)',
          <>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em' }}>Scanner</div>
            <div style={{ fontSize: '13px', fontWeight: 700 }}>{day.scanner ? `${day.scanner.start}–${day.scanner.finish}` : 'No scan'}</div>
            <div style={{ fontSize: '12px', color: '#374151' }}>
              {day.scanner ? day.scanner.hours.toFixed(2) + ' hrs' : '—'}
              {day.scanner?.startAssumed && <span style={{ color: '#b45309' }}> · start assumed</span>}
            </div>
          </>)}
        <div style={{ flex: '1 1 190px', border: dec?.source === 'manual' ? '2px solid #1a3a6b' : '1px solid #d1d5db', background: dec?.source === 'manual' ? '#eff6ff' : '#fff', borderRadius: '8px', padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em' }}>Exact time</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
            <input value={manualStart} onChange={(e) => setManualStart(e.target.value)} placeholder="HH:MM" disabled={busy}
              style={{ width: '70px', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '13px', fontWeight: 700 }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>– {finish || '?'}</span>
            <button onClick={() => save('manual', manualHours, manualStart)} disabled={busy || !(manualHours > 0)}
              style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px', border: 'none', background: manualHours > 0 ? '#1a3a6b' : '#d1d5db', color: '#fff', fontWeight: 700, fontSize: '12px', cursor: manualHours > 0 ? 'pointer' : 'default' }}>Set</button>
          </div>
          <div style={{ fontSize: '12px', color: '#374151', marginTop: '3px' }}>
            {manualHours > 0 ? manualHours.toFixed(2) + ' hrs' : 'enter a start time'}
            {day.scanner?.firstScan && (
              <button onClick={() => setManualStart(day.scanner.firstScan)} disabled={busy}
                style={{ marginLeft: '6px', background: 'none', border: 'none', color: '#1a3a6b', textDecoration: 'underline', cursor: 'pointer', fontSize: '11px', padding: 0 }}>
                use first scan {day.scanner.firstScan}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional) — e.g. driver confirmed he started at the first scan"
          disabled={busy} style={{ flex: '1 1 240px', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px' }} />
        {dec && (
          <span style={{ fontSize: '11px', color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '999px', padding: '3px 9px', fontWeight: 700 }}>
            ✓ {dec.source} · {dec.hours.toFixed(2)} hrs{dec.decidedAt ? ' · ' + dec.decidedAt.slice(0, 10) : ''}
          </span>
        )}
        {dec && <button onClick={clear} disabled={busy} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 9px', fontSize: '11px', cursor: 'pointer', color: '#6b7280' }}>Undo</button>}
      </div>
      {err && <div style={{ marginTop: '6px', fontSize: '12px', color: '#dc2626' }}>{err}</div>}
    </div>
  );
}

function HoursReviewSection({ period, data, onSaved }) {
  const [open, setOpen] = useState(false);
  const [openDriver, setOpenDriver] = useState(null);
  const review = data?.hoursReview || [];
  if (!review.length) return null;

  const s = data.summary || {};
  const dRow = openDriver ? data.drivers.find((d) => d.driver === openDriver) : null;

  return (
    <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: '10px', padding: '14px 18px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '260px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e' }}>
            ⚑ Hours review — {s.openFlagDays || 0} day{(s.openFlagDays || 0) === 1 ? '' : 's'} across {s.openFlagDrivers || 0} driver{(s.openFlagDrivers || 0) === 1 ? '' : 's'} need a call
          </div>
          <div style={{ fontSize: '12px', color: '#92400e', marginTop: '2px' }}>
            Portal and scanner conflict on these days and the evidence says one of them is wrong. About {formatMoney(s.dollarsAtRisk || 0)} of minimum pay turns on it. Everything else is agreed — nothing to do.
          </div>
        </div>
        <button onClick={() => setOpen(!open)} style={{ background: '#92400e', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
          {open ? 'Close' : 'Review hours'}
        </button>
      </div>

      {data.decisionsAvailable === false && (
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '8px 12px' }}>
          Decisions can't be saved yet — the <code>uplift_hour_decisions</code> table hasn't been created in this environment. The flags below are live; applying the migration turns the buttons on.
        </div>
      )}

      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: openDriver ? '260px 1fr' : '1fr', gap: '16px', alignItems: 'start' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            {review.map((r) => (
              <div key={r.driver} onClick={() => setOpenDriver(r.driver === openDriver ? null : r.driver)}
                style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: r.driver === openDriver ? '#eff6ff' : '#fff', borderLeft: r.driver === openDriver ? '3px solid #1a3a6b' : '3px solid transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <strong style={{ fontSize: '13px' }}>{r.driver}</strong>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: r.openFlags ? '#b45309' : '#059669' }}>
                    {r.openFlags ? formatMoney(r.dollarsAtRisk) : '✓ done'}
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>
                  {r.openFlags ? `${r.openFlags} of ${r.flaggedDays} day${r.flaggedDays === 1 ? '' : 's'} open` : `${r.flaggedDays} day${r.flaggedDays === 1 ? '' : 's'} decided`}
                  {' · '}{r.flags.map((f) => (FLAG_TEXT[f] || {}).label || f).join(' · ')}
                </div>
              </div>
            ))}
          </div>

          {dRow && (
            <div>
              <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
                <strong>{dRow.driver}</strong> — {dRow.driverName}. Deciding a day changes the minimum this driver must be paid, which moves the shortfall and any uplift you stage from it.
                The portal shift log and Driver Activity Summary keep their own records; they'll show that a decision was applied.
              </div>
              {dRow.reviewDays.map((day) => (
                <HoursReviewDay key={day.date} period={period} driver={dRow.driver} day={day} onSaved={onSaved} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UpliftWorkbenchTab({ periodEnd }) {
  const [workbenchData, setWorkbenchData] = useState(null);
  const [loadingWorkbench, setLoadingWorkbench] = useState(false);
  const [workbenchError, setWorkbenchError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [upliftUnitType, setUpliftUnitType] = useState('Parcels');
  const [upliftReason, setUpliftReason] = useState('Route Conditions');
  const [stagingLoading, setStagingLoading] = useState(false);
  const [stagingMsg, setStagingMsg] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState(null);
  const [showOnlyUnder, setShowOnlyUnder] = useState(true);
  // Self-contained period selector (mirrors Hourly Drivers): seeded from the
  // outer Pay Period picker, but selectable independently here.
  const [period, setPeriod] = useState(periodEnd || findCurrentPeriod().endISO);
  useEffect(() => { if (periodEnd) setPeriod(periodEnd); }, [periodEnd]);

  const fetchWorkbench = useCallback(async () => {
    if (!period) return;
    setLoadingWorkbench(true);
    setWorkbenchError(null);
    setSelectedDriver(null);
    setStagingMsg(null);
    setConfirmMsg(null);
    try {
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'getUpliftWorkbenchData', period }),
      });
      const json = await res.json();
      if (json.success) {
        setWorkbenchData(json);
      } else {
        setWorkbenchError(json.error || 'Unknown error loading workbench data');
      }
    } catch (err) {
      setWorkbenchError('Failed to load: ' + err.message);
    } finally {
      setLoadingWorkbench(false);
    }
  }, [period]);

  useEffect(() => { fetchWorkbench(); }, [fetchWorkbench]);

  const driver = selectedDriver ? workbenchData?.drivers?.find(d => d.driver === selectedDriver) : null;
  const qty = driver ? (driver.quantities[UNIT_TYPE_KEY[upliftUnitType]] || 0) : 0;
  const baseRate = driver ? (driver.rates[upliftUnitType === 'Parcels' ? 'Parcel' : upliftUnitType === 'Express' ? 'Exp' : upliftUnitType] || 0) : 0;
  // Remaining shortfall after already-staged uplifts (prevents a 2nd uplift on
  // another unit type re-covering the same shortfall). Backend sends it; fall
  // back to the gross gap if an older payload lacks the field.
  const shortfall = driver
    ? (driver.remainingShortfall != null
        ? driver.remainingShortfall
        : Math.max(0, -driver.variance - (driver.pendingUpliftTotal || 0)))
    : 0;
  const upliftRateRaw = qty > 0 ? shortfall / qty : 0;
  const upliftRate = qty > 0 ? ceilTo(upliftRateRaw, 2) : 0;
  const upliftTotal = Math.round(qty * upliftRate * 100) / 100;
  const overage = Math.round((upliftTotal - shortfall) * 100) / 100;
  const alreadyHasPendingForUnit = driver?.uplifts?.some(u => u.unitType === upliftUnitType && (u.status === 'Pending' || u.status === 'Confirmed'));
  const pendingCount = workbenchData?.drivers?.reduce((sum, d) => sum + (d.uplifts?.filter(u => u.status === 'Pending').length || 0), 0) || 0;

  async function stageUplift() {
    if (!driver || qty === 0 || shortfall === 0) return;
    setStagingLoading(true);
    setStagingMsg(null);
    try {
      const payload = {
        action: 'applyVariableUpliftRate',
        period,
        driver: driver.driver,
        driverName: driver.driverName || driver.driver,
        unitType: upliftUnitType,
        quantity: qty,
        baseRate: baseRate,
        upliftRate: upliftRate,
        gccdMinPayable: driver.minPayable,
        gccdActualEarned: driver.actualPayable,
        notes: upliftReason,
        appliedBy: 'paul@hollypaul.com',
      };
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', { method: 'POST', body: JSON.stringify(payload) });
      const json = await res.json();
      if (json.success) {
        const keepDriver = driver.driver;
        setStagingMsg({ type: 'success', text: `✓ Uplift staged` });
        setUpliftReason('Route Conditions');
        setTimeout(async () => {
          await fetchWorkbench();
          setSelectedDriver(keepDriver);
        }, 1500);
      } else {
        setStagingMsg({ type: 'error', text: json.error || 'Failed to stage uplift' });
      }
    } catch (err) {
      setStagingMsg({ type: 'error', text: 'Request failed: ' + err.message });
    } finally {
      setStagingLoading(false);
    }
  }
  
async function voidUplift(rowIndex, status) {
    if (status === 'Confirmed' && !window.confirm('This uplift is already CONFIRMED and will flow into the RCTI. Void it anyway?')) return;
    try {
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', { method: 'POST', body: JSON.stringify({ action: 'voidUplift', rowIndex }) });
      const json = await res.json();
      if (json.success) {
        const keepDriver = selectedDriver;
        setStagingMsg({ type: 'success', text: '✓ Uplift voided' });
        setTimeout(async () => {
          await fetchWorkbench();
          setSelectedDriver(keepDriver);
        }, 1500);
      } else {
        setStagingMsg({ type: 'error', text: json.error || 'Failed to void' });
      }
    } catch (err) {
      setStagingMsg({ type: 'error', text: 'Request failed: ' + err.message });
    }
  }

async function confirmAllPending() {
    setConfirmingAll(true);
    setConfirmMsg(null);
    try {
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', { method: 'POST', body: JSON.stringify({ action: 'confirmUplifts', period }) });
      const json = await res.json();
      if (json.success) {
        setConfirmMsg({ type: 'success', text: `✓ ${json.confirmed} uplift${json.confirmed !== 1 ? 's' : ''} confirmed — ready for RCTI generation` });
        await fetchWorkbench();
      } else {
        setConfirmMsg({ type: 'error', text: json.error || 'Failed to confirm' });
      }
    } catch (err) {
      setConfirmMsg({ type: 'error', text: 'Request failed: ' + err.message });
    } finally {
      setConfirmingAll(false);
    }
  }

  const displayedDrivers = workbenchData?.drivers?.filter(d => showOnlyUnder ? d.status === 'UNDER' : true) || [];

  // Read-only driver profile photos for the Uplift Workbench table. displayedDrivers
  // is a fresh array each render, so key the effect on a stable joined-logins string.
  const [photos, setPhotos] = useState({});
  const photoLogins = [...new Set((displayedDrivers || []).map(x => (x.driver || x.login || x.loginName || x.driverLogin || '').toUpperCase()).filter(Boolean))].join(',');
  useEffect(() => {
    const logins = photoLogins ? photoLogins.split(',') : [];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [photoLogins]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Pay period:</label>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', background: '#fff' }}>
          {PERIODS.map(p => <option key={p.endISO} value={p.endISO}>{p.label}</option>)}
        </select>
        <button onClick={fetchWorkbench} disabled={loadingWorkbench} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#1a3a6b', color: '#fff', fontSize: '14px', cursor: 'pointer', fontWeight: '600', opacity: loadingWorkbench ? 0.6 : 1 }}>
          {loadingWorkbench ? '⏳ Loading...' : '↻ Load Period'}
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151', cursor: 'pointer', marginLeft: 'auto' }}>
          <input type="checkbox" checked={showOnlyUnder} onChange={e => setShowOnlyUnder(e.target.checked)} />
          Show only UNDER minimum
        </label>
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '20px' }}>
        Minimum is based on the <strong>portal shift times</strong> the driver clocked. Scanner hours fill only the days the portal never captured, and <strong>agreed / reported hours</strong> from the Hourly Drivers page override both where entered.
      </div>

      {workbenchError && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '16px', color: '#dc2626', marginBottom: '20px' }}>{workbenchError}</div>}

      {workbenchData && (
        <>
          {workbenchData.hoursDataPresent === false && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', color: '#991b1b' }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>⚠ No worked hours for this period</div>
              <div style={{ fontSize: '13px', marginTop: '4px' }}>No portal shifts and no Wave activity, so minimum-pay figures aren't available. Check the drivers submitted their shifts, or import this period's AP activity CSV on Driver Activity Summary / Hourly Drivers, then reload.</div>
            </div>
          )}
          <HoursReviewSection period={period} data={workbenchData} onSaved={fetchWorkbench} />
          {workbenchData.summary.driversNoRate > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde047', borderRadius: '10px', padding: '12px 18px', marginBottom: '20px', color: '#92400e', fontSize: '13px', fontWeight: 600 }}>
              {workbenchData.summary.driversNoRate} driver{workbenchData.summary.driversNoRate !== 1 ? 's' : ''} have activity but no CTTCD minimum hourly rate set — set it on the Award Rates tab so they can be assessed.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '14px', marginBottom: '24px' }}>
            <SummaryCard label="Period" value={workbenchData.drivers.length} sub={`drivers in ${workbenchData.periodLabel}`} accent="#1a3a6b" />
            <SummaryCard label="Under Minimum" value={workbenchData.summary.driversUnder} sub="require uplift adjustment" accent="#dc2626" />
            <SummaryCard label="Total Shortfall" value={formatMoney(Math.abs(workbenchData.summary.totalShortfall))} sub="across all UNDER drivers" accent="#d97706" />
            <SummaryCard label="Pending Uplifts" value={formatMoney(workbenchData.summary.totalPendingUplifts)} sub={`${pendingCount} staged, awaiting confirm`} accent="#7c3aed" />
          </div>

          {pendingCount > 0 && (
            <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: '700', color: '#713f12', fontSize: '14px' }}>⚠ {pendingCount} pending uplift{pendingCount !== 1 ? 's' : ''} awaiting confirmation</div>
                <div style={{ fontSize: '13px', color: '#92400e', marginTop: '2px' }}>Confirm all pending uplifts before generating RCTIs for this period.</div>
              </div>
              <button onClick={confirmAllPending} disabled={confirmingAll} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#1a3a6b', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: confirmingAll ? 0.6 : 1 }}>
                {confirmingAll ? 'Confirming...' : '✓ Confirm All Pending'}
              </button>
            </div>
          )}

          {confirmMsg && (
            <div style={{ background: confirmMsg.type === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${confirmMsg.type === 'success' ? '#86efac' : '#fca5a5'}`, borderRadius: '8px', padding: '12px 16px', color: confirmMsg.type === 'success' ? '#166534' : '#dc2626', marginBottom: '16px', fontWeight: '600', fontSize: '14px' }}>
              {confirmMsg.text}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: selectedDriver ? '1fr 420px' : '1fr', gap: '20px', alignItems: 'start' }}>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', margin: 0 }}>Drivers — {workbenchData.periodLabel}</h2>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{displayedDrivers.length} shown</span>
              </div>
              {displayedDrivers.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>{showOnlyUnder ? 'No drivers under minimum for this period. 🎉' : 'No driver data found.'}</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        {['Driver','Company','Hours','Min Payable','Actual','Variance','Uplift Staged','Status',''].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: ['Driver','Company','Status',''].includes(h) ? 'left' : 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontSize: '12px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayedDrivers.map((d, i) => {
                        const isSelected = d.driver === selectedDriver;
                        const hasPending = (d.uplifts?.filter(u => u.status === 'Pending' || u.status === 'Confirmed').length || 0) > 0;
                        return (
                          <tr key={d.driver} onClick={() => { setSelectedDriver(isSelected ? null : d.driver); setStagingMsg(null); setUpliftUnitType('Parcels'); }} style={{ background: isSelected ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#f9fafb', cursor: 'pointer', borderLeft: isSelected ? '3px solid #1a3a6b' : '3px solid transparent' }}>
                            <td style={{ padding: '10px 14px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <DriverAvatar login={d.driver} photo={photos[(d.driver || '').toUpperCase()]} size={24} />
                                <span>{d.driver}</span>
                              </div>
                            </td>
                            <td style={{ padding: '10px 14px', color: '#6b7280', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.driverName || '—'}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap' }}>
                              {d.totalHours.toFixed(1)}
                              {d.openFlags > 0 && (
                                <span title={`${d.openFlags} day${d.openFlags === 1 ? '' : 's'} where the portal and scanner conflict — decide them in Hours review before you stage an uplift`}
                                  style={{ color: '#b45309', fontWeight: 700, marginLeft: '4px' }}>⚑</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', color: '#374151' }}>{formatMoney(d.minPayable)}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', color: '#374151' }}>{formatMoney(d.actualPayable)}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700', color: d.variance < 0 ? '#dc2626' : '#059669' }}>{formatMoney(d.variance)}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', color: hasPending ? '#7c3aed' : '#d1d5db', fontWeight: hasPending ? '700' : '400' }}>{hasPending ? formatMoney(d.pendingUpliftTotal) : '—'}</td>
                            <td style={{ padding: '10px 14px' }}><StatusBadge status={d.status} /></td>
                            <td style={{ padding: '10px 14px', color: '#1a3a6b', fontWeight: '700' }}>{isSelected ? '◀' : '▶'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {driver && (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', position: 'sticky', top: '20px' }}>
                <div style={{ background: '#1a3a6b', padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ color: '#93c5fd', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Variable Uplift Rate</div>
                    <div style={{ color: '#fff', fontSize: '16px', fontWeight: '800', marginTop: '2px' }}>{driver.driver}</div>
                    <div style={{ color: '#bfdbfe', fontSize: '12px', marginTop: '2px' }}>{driver.driverName || '—'}</div>
                  </div>
                  <button onClick={() => setSelectedDriver(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                </div>

                <div style={{ padding: '16px 18px' }}>
                  <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>4-Week Summary</div>
                    {[['Total Hours', driver.totalHours.toFixed(2) + ' hrs · ' + HOURS_BASIS_LABEL[driver.hoursBasis], '#374151'], ['Min Payable', formatMoney(driver.minPayable), '#374151'], ['Actual Earned' + (driver.actualBasis === 'hourly' ? ' (hourly pay)' : ''), formatMoney(driver.actualPayable), '#374151'], ['Variance', formatMoney(driver.variance), driver.variance < 0 ? '#dc2626' : '#059669']].map(([label, val, color]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                        <span style={{ color: '#6b7280' }}>{label}</span>
                        <span style={{ fontWeight: '700', color }}>{val}</span>
                      </div>
                    ))}
                    {driver.openFlags > 0 && (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#92400e' }}>
                        ⚑ {driver.openFlags} day{driver.openFlags === 1 ? '' : 's'} undecided — the portal and scanner conflict, and about {formatMoney(driver.dollarsAtRisk)} of this minimum turns on the call. These figures use the portal default until you decide in <strong>Hours review</strong>.
                      </div>
                    )}
                    {driver.status === 'UNDER' && (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#dc2626', fontWeight: '700' }}>Shortfall</span>
                        <span style={{ color: '#dc2626', fontWeight: '800' }}>{formatMoney(Math.abs(driver.variance))}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Weekly Detail</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          {['Week','P','E','D','DoE','Actual','Min','Var'].map(h => (
                            <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Week' ? 'left' : 'right', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {driver.weeklyDetail.map((w, i) => (
                          <tr key={w.weekStart} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                            <td style={{ padding: '6px 8px', color: '#374151', whiteSpace: 'nowrap' }}>{new Date(w.weekStart + 'T12:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{w.parcels}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{w.express}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{w.depotBags}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{w.doe}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{formatMoney(w.actualPayable)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{formatMoney(w.minPayable)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', color: w.variance < 0 ? '#dc2626' : '#059669' }}>{formatMoney(w.variance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {driver.uplifts && driver.uplifts.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Staged Uplifts</div>
                      {driver.uplifts.map((u, i) => (
                        <div key={i} style={{ background: u.status === 'Confirmed' ? '#f0fdf4' : u.status === 'Applied' ? '#eff6ff' : '#fefce8', border: `1px solid ${u.status === 'Confirmed' ? '#86efac' : u.status === 'Applied' ? '#bfdbfe' : '#fde047'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151' }}>{u.unitType} — {u.quantity.toLocaleString()} units × ${u.upliftRate.toFixed(4)}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Total: <strong>{formatMoney(u.upliftTotal)}</strong> · Status: <strong>{u.status}</strong></div>
                              {u.notes && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{u.notes}</div>}
                            </div>
                            {(u.status === 'Pending' || u.status === 'Confirmed') && (
                              <button onClick={() => voidUplift(u.rowIndex, u.status)} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', marginLeft: '8px', whiteSpace: 'nowrap' }}>Void</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {driver.status === 'UNDER' && (
                    <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Calculate Uplift</div>
                      {shortfall > 0 ? (<>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Apply uplift to unit type:</label>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {UNIT_TYPES.map(ut => {
                            const qtyForType = driver.quantities[UNIT_TYPE_KEY[ut]] || 0;
                            const isActive = upliftUnitType === ut;
                            return (
                              <button key={ut} onClick={() => setUpliftUnitType(ut)} disabled={qtyForType === 0} style={{ padding: '6px 12px', borderRadius: '6px', border: `2px solid ${isActive ? '#1a3a6b' : '#e5e7eb'}`, background: isActive ? '#1a3a6b' : '#fff', color: isActive ? '#fff' : qtyForType === 0 ? '#d1d5db' : '#374151', fontSize: '12px', fontWeight: '600', cursor: qtyForType === 0 ? 'not-allowed' : 'pointer' }}>
                                {ut} ({qtyForType.toLocaleString()})
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {qty > 0 ? (
                        <div style={{ background: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', padding: '12px', marginBottom: '12px' }}>
                          {[['Shortfall', formatMoney(shortfall)], ['Quantity', qty.toLocaleString() + ' ' + upliftUnitType], ['Base Rate', formatMoney(baseRate) + ' / unit'], ['Uplift Rate (CEILING)', '$' + upliftRate.toFixed(4) + ' / unit'], ['Uplift Total', formatMoney(upliftTotal)], ['Overage (rounds up to)', formatMoney(overage)]].map(([label, val]) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                              <span style={{ color: '#6b7280' }}>{label}</span>
                              <span style={{ fontWeight: label.includes('Uplift Rate') || label.includes('Uplift Total') ? '800' : '600', color: label.includes('Uplift Total') ? '#1a3a6b' : '#374151' }}>{val}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '12px' }}>No {upliftUnitType} quantity for this driver in this period.</div>
                      )}
<div style={{ marginBottom: '12px' }}>
  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Uplift reason:</label>
  <select value={upliftReason} onChange={e => setUpliftReason(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', boxSizing: 'border-box', background: '#fff', color: '#374151' }}>
    <option>Route Conditions</option>
    <option>Route Establishment</option>
    <option>Prevailing Conditions</option>
  </select>
</div>
                      {stagingMsg && (
                        <div style={{ background: stagingMsg.type === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${stagingMsg.type === 'success' ? '#86efac' : '#fca5a5'}`, borderRadius: '6px', padding: '8px 12px', color: stagingMsg.type === 'success' ? '#166534' : '#dc2626', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>
                          {stagingMsg.text}
                        </div>
                      )}
                      {alreadyHasPendingForUnit ? (
                        <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '6px', padding: '8px 12px', color: '#92400e', fontSize: '12px', fontWeight: '600' }}>
                          An active uplift already exists for {upliftUnitType}. Void it first to replace.
                        </div>
                      ) : (
                        <button onClick={stageUplift} disabled={stagingLoading || qty === 0 || shortfall === 0} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: (qty === 0 || shortfall === 0) ? '#e5e7eb' : '#1a3a6b', color: (qty === 0 || shortfall === 0) ? '#9ca3af' : '#fff', fontSize: '14px', fontWeight: '800', cursor: (qty === 0 || shortfall === 0) ? 'not-allowed' : 'pointer', opacity: stagingLoading ? 0.6 : 1 }}>
                          {stagingLoading ? 'Staging...' : `Stage Uplift — ${formatMoney(upliftTotal)}`}
                        </button>
                      )}
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>Staged uplifts are Pending until you click "Confirm All Pending" above.</div>
                      </>) : (
                        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '10px 12px', color: '#166534', fontSize: '12px', fontWeight: 600 }}>
                          ✓ Shortfall fully covered by staged uplift{(driver.uplifts && driver.uplifts.length > 1) ? 's' : ''} ({formatMoney(driver.pendingUpliftTotal)}). Void above to adjust.
                        </div>
                      )}
                    </div>
                  )}

                  {driver.status !== 'UNDER' && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '12px', textAlign: 'center', color: '#166534', fontWeight: '700', fontSize: '13px' }}>
                      ✓ Driver meets or exceeds minimum — no uplift required
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!workbenchData && !loadingWorkbench && !workbenchError && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280', background: '#f9fafb', borderRadius: '10px' }}>Select a period and click Load Period to begin.</div>
      )}
    </div>
  );
}

// ── Award Rates Tab ────────────────────────────────────────────────────────────

const APPSSCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzOBv0uBlIglllBCIh8AQHJZKTzRF7jrfVe5I7qr2w8Tc4oy9QnFRsVS0gb9EprnxMliw/exec';

const VAN_CATEGORIES = [
  {
    id: 'micro',
    label: 'Micro / Car',
    capacity: 'Up to 750 kg',
    example: 'Amazon Flex, small hatchback',
    rateJul2025: 37.80,
    rateJul2025Confirmed: true,
    rateJul2025Estimated: false,
    rateMar2022: 29.81,
    color: '#7c3aed',
  },
  {
    id: 'small',
    label: 'Small Van',
    capacity: '750 kg – 1 t',
    example: 'Toyota HiAce, Mitsubishi Express',
    rateJul2025: 39.15,
    rateJul2025Confirmed: true,
    rateJul2025Estimated: false,
    rateMar2022: 30.59,
    color: '#1a3a6b',
  },
  {
    id: 'medium',
    label: 'Medium Van',
    capacity: '1 t – 1.5 t',
    example: 'Ford Transit, LDV V80',
    rateJul2025: 41.04,
    rateJul2025Confirmed: true,
    rateJul2025Estimated: false,
    rateMar2022: 32.00,
    color: '#059669',
  },
  {
    id: 'large',
    label: 'Large Van',
    capacity: '1.5 t – 3 t',
    example: 'Mercedes Sprinter, Iveco Daily',
    rateJul2025: 43.74,
    rateJul2025Confirmed: true,
    rateJul2025Estimated: false,
    rateMar2022: 33.33,
    color: '#d97706',
  },
];

const PHASE_IN_MILESTONES = [
  { date: 'Pre-2022', label: 'Historical flat rate', note: 'Single rate ~$28/hr, set approx. 2006–07. No separate light-vehicle categories.', status: 'past' },
  { date: '1 Mar 2022', label: 'Light vehicle rates introduced', note: 'NSW IRC inserted 4 new light-vehicle categories into the Courier & Taxi Truck Contract Determination (CTTCD). Phase-in commenced.', status: 'past' },
  { date: '1 Jul 2022', label: 'First annual increase', note: '~6.9% increase for 750 kg–1 t vehicles; ~8% increase for 1–1.5 t vehicles.', status: 'past' },
  { date: '1 Jul 2023', label: 'Second annual increase', note: '~10% increase for lower carrying-capacity vehicles; further significant increases across categories.', status: 'past' },
  { date: '1 Jul 2024', label: 'Third annual increase', note: 'Continued phase-in across all light-vehicle categories.', status: 'past' },
  { date: '1 Jul 2025', label: 'Full implementation complete', note: 'Phase-in period ends. All categories reach final minimum rates. Up to 750 kg → $37.80/hr; 750 kg–1 t → $39.15/hr; 1–1.5 t → $41.04/hr; 1.5–3 t → $43.74/hr. (Source: IRC Gazette C9406)', status: 'current' },
  { date: '1 Jul 2026', label: 'Next IRC annual review', note: 'NSW Industrial Relations Commission expected to conduct annual wage review. Updated rates to be confirmed.', status: 'future' },
];

const HIDDEN_DRIVERS_KEY = 'hp-gccd-hidden-drivers';

function AwardRatesTab() {
  const [liveRates, setLiveRates] = useState(null);
  const [loadingLive, setLoadingLive] = useState(true);
  const [liveError, setLiveError] = useState(null);
  const [editingLogin, setEditingLogin] = useState(null);
  const [editVanSize, setEditVanSize] = useState('');
  const [editRate, setEditRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [hiddenDrivers, setHiddenDrivers] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HIDDEN_DRIVERS_KEY) || '[]'); } catch { return []; }
  });
  const [lastChecked, setLastChecked] = useState(() => localStorage.getItem('hp-gccd-last-checked') || null);

  const loadRates = useCallback(async () => {
    setLoadingLive(true);
    setLiveError(null);
    try {
      const res = await fetch('/.netlify/functions/sheets');
      const json = await res.json();
      if (json.success) {
        setLiveRates({ gccdRates: json.gccdRates || {}, drivers: json.drivers || [] });
      } else {
        setLiveError(json.error || 'Failed to load rates');
      }
    } catch (err) {
      setLiveError(err.message);
    } finally {
      setLoadingLive(false);
    }
  }, []);

  useEffect(() => { loadRates(); }, [loadRates]);

  const persistHidden = (list) => {
    setHiddenDrivers(list);
    localStorage.setItem(HIDDEN_DRIVERS_KEY, JSON.stringify(list));
  };

  const hideDriver = (login) => persistHidden([...hiddenDrivers, login]);
  const unhideDriver = (login) => persistHidden(hiddenDrivers.filter(l => l !== login));

  const allDriverRateRows = liveRates
    ? (() => {
        const rated = Object.entries(liveRates.gccdRates).map(([login, r]) => {
          const driverInfo = liveRates.drivers.find(d => (d.loginName || '').toUpperCase() === login);
          const vanSize = r.vanSize || 'Small Van';
          const vanCat = VAN_CATEGORIES.find(c => vanSize.toLowerCase().includes(c.id)) || VAN_CATEGORIES[1];
          const benchmark = vanCat.rateJul2025;
          const configured = r.minHourlyRate || 0;
          const status = !benchmark ? 'unknown' : configured >= benchmark ? 'ok' : 'low';
          const isActive = driverInfo ? driverInfo.active !== false : true;
          const isHidden = hiddenDrivers.includes(login);
          return { login, driverInfo, configured, vanSize, benchmark, status, vanCat, isActive, isHidden };
        });
        // Active drivers with NO GCCD_Rates row yet — surface them so a rate can be set.
        // Without a row they're invisible AND get zero min-pay enforcement (silent underpayment).
        const rated_logins = new Set(rated.map(r => r.login));
        const unrated = (liveRates.drivers || [])
          .filter(d => d.active !== false && (d.loginName || '').trim())
          .map(d => (d.loginName || '').toUpperCase())
          .filter(lg => !rated_logins.has(lg))
          .map(lg => {
            const driverInfo = liveRates.drivers.find(d => (d.loginName || '').toUpperCase() === lg);
            return { login: lg, driverInfo, configured: 0, vanSize: '', benchmark: null, status: 'unset', vanCat: VAN_CATEGORIES[1], isActive: true, isHidden: hiddenDrivers.includes(lg) };
          });
        return [...rated, ...unrated].sort((a, b) => a.login.localeCompare(b.login));
      })()
    : [];
  const unsetCount = allDriverRateRows.filter(r => r.status === 'unset' && !r.isHidden).length;

  const visibleRows = allDriverRateRows.filter(r =>
    !r.isHidden && (showInactive || r.isActive)
  );
  const hiddenCount = allDriverRateRows.filter(r => r.isHidden).length;

  async function saveEdit(login) {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch('/.netlify/functions/gccd-uplift-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'updateGCCDRate', driver: login, minHourlyRate: parseFloat(editRate), vanSize: editVanSize }),
      });
      const json = await res.json();
      if (json.success) {
        setSaveMsg({ type: 'success', text: '✓ Saved' });
        setEditingLogin(null);
        setTimeout(() => { loadRates(); setSaveMsg(null); }, 1500);
      } else {
        setSaveMsg({ type: 'error', text: json.error || 'Save failed' });
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  function startEdit(row) {
    setEditingLogin(row.login);
    const vs = row.vanSize || 'Small Van';   // new (unset) drivers default to Small Van; user picks the real size
    setEditVanSize(vs);
    const cat = VAN_CATEGORIES.find(c => c.label === vs);
    setEditRate(row.configured ? row.configured.toFixed(2) : (cat ? cat.rateJul2025.toFixed(2) : '0.00'));
    setSaveMsg(null);
  }

  function markChecked() {
    const now = new Date().toISOString();
    setLastChecked(now);
    localStorage.setItem('hp-gccd-last-checked', now);
    window.open('http://www.ircgazette.justice.nsw.gov.au/irc/ircgazette.nsf/webviewdate/C9406', '_blank');
  }

  const daysSinceCheck = lastChecked
    ? Math.floor((Date.now() - new Date(lastChecked).getTime()) / 86400000)
    : null;

  return (
    <div>
      {/* Award instrument banner */}
      <div style={{ background: 'linear-gradient(135deg, #1a3a6b 0%, #2d5aad 100%)', borderRadius: '12px', padding: '24px 28px', marginBottom: '28px', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#93c5fd', marginBottom: '8px' }}>Applicable Instrument</div>
            <div style={{ fontSize: '20px', fontWeight: '800', marginBottom: '6px' }}>NSW Transport Industry — Courier and Taxi Truck Contract Determination</div>
            <div style={{ fontSize: '13px', color: '#bfdbfe', marginBottom: '16px' }}>
              Light vehicle minimum rates inserted 1 March 2022 — phased implementation completed 1 July 2025 &nbsp;·&nbsp; All-in hourly rates (vehicle, fuel &amp; labour — no separate per-km rate)
            </div>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              {[
                { label: 'Jurisdiction', value: 'NSW (state)' },
                { label: 'Administered by', value: 'NSW Industrial Relations Commission (IRC)' },
                { label: 'Phase-in period', value: '1 Mar 2022 → 1 Jul 2025' },
                { label: 'Review cycle', value: 'Annual (IRC, ~July)' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: '10px', color: '#93c5fd', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginTop: '2px' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            <button
              onClick={markChecked}
              style={{ padding: '10px 18px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              ↻ Check for Updates
            </button>
            <a
              href="http://www.ircgazette.justice.nsw.gov.au/irc/ircgazette.nsf/webviewdate/C9406"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '12px', color: '#bfdbfe', textDecoration: 'underline', textAlign: 'right' }}
            >
              View Award on NSW IRC ↗
            </a>
            {daysSinceCheck !== null && (
              <div style={{ fontSize: '11px', color: '#93c5fd', textAlign: 'right' }}>
                Last checked: {daysSinceCheck === 0 ? 'today' : `${daysSinceCheck}d ago`}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Van size categories & rates */}
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '14px' }}>Light Vehicle Categories — Minimum Hourly Rates (excl. GST)</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '14px' }}>
          {VAN_CATEGORIES.map(cat => (
            <div key={cat.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', borderTop: `4px solid ${cat.color}`, padding: '18px 20px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>{cat.capacity}</div>
              <div style={{ fontSize: '17px', fontWeight: '800', color: cat.color, marginBottom: '4px' }}>{cat.label}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '14px' }}>{cat.example}</div>
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>From 1 Jul 2025</span>
                  <span style={{ fontSize: '14px', fontWeight: '800', color: cat.rateJul2025Confirmed ? (cat.rateJul2025Estimated ? '#d97706' : '#111827') : '#9ca3af' }}>
                    {cat.rateJul2025Confirmed
                      ? (cat.rateJul2025Estimated ? `~$${cat.rateJul2025.toFixed(2)}/hr` : `$${cat.rateJul2025.toFixed(2)}/hr`)
                      : '— verify with IRC'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>From 1 Mar 2022</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: cat.rateMar2022 ? '#374151' : '#9ca3af' }}>
                    {cat.rateMar2022 ? `$${cat.rateMar2022.toFixed(2)}/hr` : '—'}
                  </span>
                </div>
                {cat.rateJul2025Confirmed
                  ? cat.rateJul2025Estimated
                    ? <div style={{ padding: '4px 8px', borderRadius: '6px', background: '#fffbeb', fontSize: '11px', color: '#92400e', fontWeight: '600', textAlign: 'center' }}>~ Estimated — confirm with IRC</div>
                    : <div style={{ padding: '4px 8px', borderRadius: '6px', background: '#eff6ff', fontSize: '11px', color: '#3b82f6', fontWeight: '600', textAlign: 'center' }}>✓ Confirmed rate</div>
                  : <div style={{ padding: '4px 8px', borderRadius: '6px', background: '#fffbeb', fontSize: '11px', color: '#92400e', fontWeight: '600', textAlign: 'center' }}>Verify with IRC</div>
                }
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#92400e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <span><strong>All rates confirmed</strong> from IRC Gazette C9406 — Transport Industry Courier and Taxi Truck Contract Determination. Effective from first full pay period on or after 1 July 2025.</span>
          <a href="http://www.ircgazette.justice.nsw.gov.au/irc/ircgazette.nsf/webviewdate/C9406" target="_blank" rel="noopener noreferrer" style={{ color: '#92400e', fontWeight: '700', whiteSpace: 'nowrap' }}>View Gazette C9406 ↗</a>
        </div>
      </div>

      {/* Phase-in timeline */}
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '14px' }}>Rate History &amp; Phase-In Timeline</h2>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
          {PHASE_IN_MILESTONES.map((m, i) => {
            const isCurrent = m.status === 'current';
            const isFuture = m.status === 'future';
            return (
              <div key={i} style={{ display: 'flex', gap: '16px', padding: '14px 20px', borderBottom: i < PHASE_IN_MILESTONES.length - 1 ? '1px solid #f3f4f6' : 'none', background: isCurrent ? '#eff6ff' : 'transparent', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: '110px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: isCurrent ? '#1a3a6b' : isFuture ? '#9ca3af' : '#374151' }}>{m.date}</div>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isCurrent ? '#1a3a6b' : isFuture ? '#d1d5db' : '#9ca3af', marginTop: '6px', marginLeft: '4px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: isCurrent ? '#1a3a6b' : isFuture ? '#9ca3af' : '#374151' }}>{m.label}</span>
                    {isCurrent && <span style={{ fontSize: '10px', background: '#1a3a6b', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontWeight: '700', letterSpacing: '0.05em' }}>CURRENT</span>}
                    {isFuture && <span style={{ fontSize: '10px', background: '#f3f4f6', color: '#6b7280', borderRadius: '10px', padding: '2px 8px', fontWeight: '700', letterSpacing: '0.05em' }}>UPCOMING</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: isFuture ? '#9ca3af' : '#6b7280' }}>{m.note}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Configured rates table */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', margin: 0 }}>Your Configured Rates</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {hiddenCount > 0 && (
              <button onClick={() => persistHidden([])} style={{ fontSize: '12px', color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer' }}>
                Show {hiddenCount} hidden
              </button>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show inactive drivers
            </label>
            <button onClick={loadRates} disabled={loadingLive} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#1a3a6b', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '600', opacity: loadingLive ? 0.6 : 1 }}>
              ↻ Refresh
            </button>
          </div>
        </div>

        {unsetCount > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', color: '#92400e', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
            ⚠ {unsetCount} active driver{unsetCount > 1 ? 's have' : ' has'} no CTTCD rate set — without a rate they get <strong>no minimum-pay enforcement</strong>. Click ✏ to assign a van size + rate (rebuild that period's RCTIs afterwards to apply any top-up).
          </div>
        )}

        {saveMsg && (
          <div style={{ background: saveMsg.type === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${saveMsg.type === 'success' ? '#86efac' : '#fca5a5'}`, borderRadius: '8px', padding: '10px 14px', color: saveMsg.type === 'success' ? '#166534' : '#dc2626', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
            {saveMsg.text}
          </div>
        )}

        {loadingLive && <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading configured rates...</div>}
        {liveError && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '14px', color: '#dc2626', fontSize: '13px' }}>Could not load configured rates: {liveError}</div>}
        {!loadingLive && !liveError && visibleRows.length === 0 && (
          <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '40px', textAlign: 'center', color: '#6b7280' }}>No driver rates to display.</div>
        )}
        {!loadingLive && !liveError && visibleRows.length > 0 && (
          <>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Driver', 'Company', 'Van Size', 'Configured Rate', 'Award Min (Jul 2025)', 'Difference', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: ['Driver', 'Company', 'Van Size', 'Status', ''].includes(h) ? 'left' : 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r, i) => {
                    const isEditing = editingLogin === r.login;
                    const diff = r.benchmark ? r.configured - r.benchmark : null;
                    return (
                      <tr key={r.login} style={{ background: isEditing ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#f9fafb', opacity: r.isActive ? 1 : 0.55 }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' }}>
                          {r.login}
                          {!r.isActive && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#f3f4f6', color: '#9ca3af', borderRadius: '8px', padding: '1px 6px' }}>inactive</span>}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6b7280' }}>{r.driverInfo?.companyName || '—'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          {isEditing
                            ? <select value={editVanSize} onChange={e => { const v = e.target.value; setEditVanSize(v); const cat = VAN_CATEGORIES.find(c => c.label === v); if (cat) setEditRate(cat.rateJul2025.toFixed(2)); }} style={{ padding: '4px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '12px', color: '#374151' }}>
                                {VAN_CATEGORIES.map(c => <option key={c.id} value={c.label}>{c.label}</option>)}
                              </select>
                            : <span style={{ color: r.vanSize ? '#374151' : '#d97706' }}>{r.vanSize || 'Set rate →'}</span>
                          }
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700', color: '#111827' }}>
                          {isEditing
                            ? <input type="number" step="0.01" value={editRate} onChange={e => setEditRate(e.target.value)} style={{ width: '80px', padding: '4px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', textAlign: 'right' }} />
                            : `$${r.configured.toFixed(2)}/hr`
                          }
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: '#6b7280' }}>
                          {r.benchmark ? `$${r.benchmark.toFixed(2)}/hr` : <span style={{ color: '#d1d5db' }}>—</span>}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700', color: diff === null ? '#9ca3af' : diff >= 0 ? '#059669' : '#dc2626' }}>
                          {diff === null ? '—' : (diff >= 0 ? '+' : '') + `$${diff.toFixed(2)}`}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {r.status === 'ok' && <span style={{ background: '#d1fae5', color: '#065f46', fontSize: '11px', fontWeight: '700', borderRadius: '10px', padding: '3px 10px', whiteSpace: 'nowrap' }}>✓ At / Above Min</span>}
                          {r.status === 'low' && <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '11px', fontWeight: '700', borderRadius: '10px', padding: '3px 10px', whiteSpace: 'nowrap' }}>⚠ Below Min</span>}
                          {r.status === 'unknown' && <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: '11px', fontWeight: '700', borderRadius: '10px', padding: '3px 10px', whiteSpace: 'nowrap' }}>Verify rate</span>}
                          {r.status === 'unset' && <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '11px', fontWeight: '700', borderRadius: '10px', padding: '3px 10px', whiteSpace: 'nowrap' }}>⚠ No rate set</span>}
                        </td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => saveEdit(r.login)} disabled={saving} style={{ padding: '4px 12px', borderRadius: '5px', border: 'none', background: '#1a3a6b', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                                {saving ? '…' : 'Save'}
                              </button>
                              <button onClick={() => setEditingLogin(null)} style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer' }}>✕</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => startEdit(r)} style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer' }} title="Edit rate">✏</button>
                              <button onClick={() => hideDriver(r.login)} style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #e5e7eb', background: '#fff', color: '#9ca3af', fontSize: '12px', cursor: 'pointer' }} title="Hide this driver">✕</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#9ca3af', textAlign: 'right' }}>
              Showing {visibleRows.length} of {allDriverRateRows.length} drivers · Source: gccd_rates (Supabase)
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { id: 'history', label: '📊 CTTCD History' },
  { id: 'uplift', label: '⚡ Uplift Workbench' },
  { id: 'rates', label: '🏛 Award Rates' },
  { id: 'parcel-rates', label: '📦 Parcel Rates' },
];

export default function GCCDReportPage() {
  const [activeTab, setActiveTab] = useState('history');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(() => findCurrentPeriod());

  const fetchGCCDHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/sheets');
      const json = await res.json();
      if (json.success && Array.isArray(json.gccdHistory)) {
        setRecords(json.gccdHistory);
      } else {
        setError('No CTTCD history records found. Build RCTIs to populate CTTCD history.');
      }
    } catch (err) {
      setError('Failed to load CTTCD history: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGCCDHistory(); }, [fetchGCCDHistory]);

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1a3a6b', margin: '0 0 4px' }}>CTTCD Reconciliation</h1>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Minimum Contract Remuneration Order — compliance reporting and Variable Uplift Rate management</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Pay Period</label>
          <select
            value={selectedPeriod.endISO}
            onChange={e => {
              const p = PERIODS.find(p => p.endISO === e.target.value);
              if (p) setSelectedPeriod(p);
            }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', color: '#374151', background: '#fff', minWidth: 260 }}
          >
            {PERIODS.map(p => <option key={p.endISO} value={p.endISO}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: activeTab === tab.id ? '700' : '500', color: activeTab === tab.id ? '#1a3a6b' : '#6b7280', borderBottom: activeTab === tab.id ? '3px solid #1a3a6b' : '3px solid transparent', marginBottom: '-2px', transition: 'all 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'history' && <GCCDHistoryTab records={records} loading={loading} error={error} onRefresh={fetchGCCDHistory} selectedPeriodISO={selectedPeriod.endISO} />}
      {activeTab === 'uplift' && <UpliftWorkbenchTab periodEnd={selectedPeriod.endISO} />}
      {activeTab === 'rates' && <AwardRatesTab />}
      {activeTab === 'parcel-rates' && <ParcelRatesTab />}
    </div>
  );
}
